/**
 * Response Strategy Service
 * Handles response-strategy-related API calls
 */

import api from '@/lib/api';
import { withBuScope } from '@/lib/business-unit-scope';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { ResponseStrategyAdapter } from '@/adapters';
import type {
	ApiResponse,
	ResponseStrategy,
	CreateResponseStrategyDto,
	UpdateResponseStrategyDto,
	ResponseStrategyResponseDto,
	ResponseStrategiesListResponseDto,
	GenerateResponseStrategyResult,
	GenerateResponseStrategyResponseDto
} from '@/types';

type ResponseStrategyListFilters = {
	intent?: string;
	status?: 'active' | 'draft' | 'disabled';
};

export const responseStrategyService = {
	/**
	 * Get response strategies for the current user's account
	 */
	async getResponseStrategies(
		filters?: ResponseStrategyListFilters,
		scope?: { bu_id?: string }
	): Promise<{ strategies: ResponseStrategy[]; count: number }> {
		const query = new URLSearchParams();
		if (scope?.bu_id) query.set('bu_id', scope.bu_id);
		if (filters?.intent) query.set('intent', filters.intent);
		if (filters?.status) query.set('status', filters.status);

		const url =
			query.size > 0 ? `${API_ENDPOINTS.RESPONSE_STRATEGIES.BASE}?${query.toString()}` : API_ENDPOINTS.RESPONSE_STRATEGIES.BASE;
		const response = await api.get<ApiResponse<ResponseStrategiesListResponseDto>>(url);
		return ResponseStrategyAdapter.fromListApi(response.data.data);
	},

	/**
	 * Get a single response strategy by ID
	 */
	async getResponseStrategyById(id: string): Promise<ResponseStrategy> {
		const response = await api.get<ApiResponse<ResponseStrategyResponseDto>>(API_ENDPOINTS.RESPONSE_STRATEGIES.BY_ID(id));
		return ResponseStrategyAdapter.fromApi(response.data.data);
	},

	/**
	 * Create a new response strategy
	 */
	async createResponseStrategy(
		data: CreateResponseStrategyDto,
		buScope: { bu_id?: string } = {},
	): Promise<ResponseStrategy> {
		const payload = ResponseStrategyAdapter.toCreateRequest(data);
		const response = await api.post<ApiResponse<ResponseStrategyResponseDto>>(
			withBuScope(API_ENDPOINTS.RESPONSE_STRATEGIES.BASE, buScope),
			payload,
		);
		return ResponseStrategyAdapter.fromApi(response.data.data);
	},

	/**
	 * Update an existing response strategy
	 */
	async updateResponseStrategy(id: string, data: UpdateResponseStrategyDto): Promise<ResponseStrategy> {
		const payload = ResponseStrategyAdapter.toUpdateRequest(data);
		const response = await api.put<ApiResponse<ResponseStrategyResponseDto>>(
			API_ENDPOINTS.RESPONSE_STRATEGIES.BY_ID(id),
			payload
		);
		return ResponseStrategyAdapter.fromApi(response.data.data);
	},

	/**
	 * Delete a response strategy
	 */
	async deleteResponseStrategy(id: string): Promise<void> {
		await api.delete<ApiResponse<void>>(API_ENDPOINTS.RESPONSE_STRATEGIES.BY_ID(id));
	},

	/**
	 * Set a response strategy's active state. Enabling clears draft; disabling
	 * only clears active (a paused strategy is "disabled", not "draft").
	 */
	async setResponseStrategyActive(id: string, isActive: boolean): Promise<ResponseStrategy> {
		const response = await api.patch<ApiResponse<ResponseStrategyResponseDto>>(API_ENDPOINTS.RESPONSE_STRATEGIES.STATUS(id), {
			isActive
		});
		return ResponseStrategyAdapter.fromApi(response.data.data);
	},

	/**
	 * Generate a response strategy via the harness LLM module — does not persist
	 */
	async generateResponseStrategy(input: { intents: string[]; merchantAsk: string }): Promise<GenerateResponseStrategyResult> {
		const response = await api.post<ApiResponse<GenerateResponseStrategyResponseDto>>(
			API_ENDPOINTS.RESPONSE_STRATEGIES.GENERATE,
			input
		);
		return response.data.data;
	},

	/**
	 * Create a new response strategy by copying a global template
	 */
	/**
	 * Templates are account-wide; the STRATEGY made from one belongs to a brand, so the
	 * scope has to ride along or every brand's copy lands on the shared row.
	 */
	async createResponseStrategyFromTemplate(
		templateId: string,
		buScope: { bu_id?: string } = {},
	): Promise<ResponseStrategy> {
		const response = await api.post<ApiResponse<ResponseStrategyResponseDto>>(
			withBuScope(API_ENDPOINTS.RESPONSE_STRATEGIES.FROM_TEMPLATE(templateId), buScope),
			{}
		);
		return ResponseStrategyAdapter.fromApi(response.data.data);
	}
};
